// Import the RTK Query methods from the React-specific entry point
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

const apiToken = localStorage.getItem('access_token')
const widgetScript = localStorage.getItem('widgetScript')

// Define our single API slice object
export const apiSlice = createApi({
  // The cache reducer expects to be added at `state.api`
  reducerPath: 'api',
  // All of our requests will have URLs starting with '/api'
  baseQuery: fetchBaseQuery({ baseUrl: process.env.REACT_APP_BASE_APP_URL }),
  keepUnusedDataFor: 600,
  // The "endpoints" represent operations and requests for this server
  endpoints: builder => ({
    // The `getLeads` endpoint is a "query" operation that returns data
    getLeads: builder.query({
      // The URL for the request is '/api/v1/leads'
      query: args => ({
        url: '/api/v1/leads',
        params: args,
        method: 'GET',
        headers: { Authorization: `Bearer ${apiToken}` }
      })
    }),
    getTags: builder.query({
      // The URL for the request is '/api/v1/widget'
      query: widget => ({
        url: `/api/v1/widget/${widget}/tags`,
        method: 'GET',
        headers: { Authorization: `Bearer ${apiToken}` },
        processData: false,
        mimeType: 'multipart/form-data',
        contentType: false,
        timeout: 0
      })
    }),
    getSegments: builder.query({
      // The URL for the request is '/api/v1/list/lists'
      query: () => ({
        url: `/api/v1/list/lists`,
        method: 'GET',
        headers: { Authorization: `Bearer ${apiToken}` }
      })
    }),
    getLead: builder.query({
      // The URL for the request is '/api/v1/leads/lead/${id}'
      query: id => ({
        url: `/api/v1/leads/lead/${id}`,
        method: 'GET',
        headers: { Authorization: `Bearer ${apiToken}` }
      })
    }),
    getConversation: builder.query({
      // The URL for the request is '/api/v1/leads/lead/${id}'
      query: id => ({
        url: `/api/v1/conversation/${id}`,
        method: 'GET',
        headers: { Authorization: `Bearer ${apiToken}` }
      })
    }),
    getLeadNotes: builder.query({
      // The URL for the request is '/api/v1/leads/lead/${id}'
      query: id => ({
        url: `/api/v1/leads/${id}/note`,
        method: 'GET',
        headers: { Authorization: `Bearer ${apiToken}` }
      })
    }),
    getUserId: builder.query({
      // The URL for the request is '/api/v1/user/caller-id'
      query: () => ({
        url: `/api/v1/user/caller-id`,
        method: 'GET',
        headers: { Authorization: `Bearer ${apiToken}` }
      })
    }),
    getUserIdWithName: builder.query({
      // The URL for the request is 'api/v1/user/caller-id-with-names'
      query: () => ({
        url: `api/v1/user/caller-id-with-names?support_sms=1`,
        method: 'GET',
        headers: { Authorization: `Bearer ${apiToken}` }
      })
    }),
    getWidget: builder.query({
      // The URL for the request is '/api/v1/widget/get-widget-call-settings?widget_id=${id}'
      query: id => ({
        url: `/api/v1/widget/get-widget-call-settings?widget_id=${id}`,
        method: 'GET',
        headers: { Authorization: `Bearer ${apiToken}` }
      })
    }),
    getConversations: builder.query({
      // The URL for the request is '/api/v1/widget/get-widget-call-settings?widget_id=${id}'
      query: page => ({
        url: `/api/v1/conversation/fetch-conversations?script_id=${widgetScript}&direction=desc&page=${page}`,
        method: 'GET',
        headers: { Authorization: `Bearer ${apiToken}` }
      })
    }),
    getContacts: builder.query({
      // The URL for the request is '/api/v1/contact/filter-contacts-by-date'
      query: data => {
        let body = { date: 'all', type: 'all' }
        if (data?.date) {
          body = data;
        }
        return ({
        url: `/api/v1/contact/filter-contacts-by-date`,
        body,
        method: 'POST',
        headers: { Authorization: `Bearer ${apiToken}` }
      })}
    })
  })
})

// Export the auto-generated hook for the
//`getLeads,getTags,getSegments,getLead,getUserId,getWidget` query endpoint
export const {
  endpoints,
  useGetLeadsQuery,
  useGetTagsQuery,
  useGetLeadNotesQuery,
  useGetSegmentsQuery,
  useGetLeadQuery,
  useGetUserIdQuery,
  useGetUserIdWithNameQuery,
  useGetWidgetQuery,
  useGetContactsQuery,
  useGetConversationQuery,
  useGetConversationsQuery,
} = apiSlice
